import {
  addPersonalInfoAdmin,
  updatePersonalInfoAdmin,
  getPersonalInfoAdmin,
  getIdentificationAdmin,
  addLicIdentificationAdmin,
  updateLicIdentificationAdmin,
  addLicEmploymentAdmin,
  updateLicEmploymentAdmin,
  listLicEmploymentAdmin,
  listLicOtherPolicyAdmin,
  addLicOtherPolicyAdmin,
  addLicInsuranceHistoryAdmin,
  updateLicOtherPolicyAdmin,
  listLicNomineeAdmin,
  addLicNomineeAdmin,
  updateLicNomineeAdmin,
  listLicHealthAdmin,
  addLicHealthAdmin,
  updateLicHealthAdmin,
  listLicLegalAdmin,
  addLicLegalAdmin,
  updateLicLegalAdmin,
  getLicDetailById,
  updateNewLicProposalAdmin,
  addLicDocumentAdmin,
  updateLicDocumentAdmin,
  deleteLicDocumentAdmin,
  listLicDocumentsAdmin,
  listLicDocumentTypeAdmin,
  listLicProposalAdmin,
  deleteLicProposalAdmin,
  runKycDocumentFlow,
} from "services/kycService";
import type {
  AddPersonalInfoParams,
  AddLicEmploymentParams,
  UpdateLicEmploymentParams,
  AddLicLegalParams,
  UpdateLicLegalParams,
  AddLicHealthParams,
  UpdateLicHealthParams,
  AddLicNomineeParams,
  UpdateLicNomineeParams,
  AddLicOtherPolicyParams,
  LicInsuranceHistory,
  UpdateLicOtherPolicyParams,
  AddLicDocumentParams,
  UpdateLicDocumentParams,
  ListLicDocumentsParams,
  AddLicIdentificationParams,
  UpdateLicIdentificationParams,
  UpdateNewLicProposalParams,
} from "services/kycService";
import {
  setLoader,
  successMsg,
  errorMsg,
  setLicId,
  setEmploymentId,
  setLegalId,
  setHealthId,
  setNomineeId,
  setOtherPolicyId,
  setDocumentId,
  setDocumentData,
  setIdentificationId,
  setDocumentTypeData,
  setEmploymentData,
  setPersonalInfoData,
  setIdentificationData,
  setOtherPolicyData,
  setNomineeData,
  setHealthData,
  setLegalData,
  setLicDetailData,
  setProposalList,
  setLoadingProposals,
} from "./reducer";
import type { AppDispatch } from "store";

export const addPersonalInfoAdminThunk =
  (data: AddPersonalInfoParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; lic_id?: number; message?: string }> => {
    dispatch(setLoader(true));
    try {
      const response = await addPersonalInfoAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "Saved personal detail successfully.",
            id: Date.now(),
          })
        );
        dispatch(setLicId(response.data));
        return { success: true, lic_id: response.data };
      }
      const msg = response.message ?? "Failed to save personal detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updatePersonalInfoAdminThunk =
  (lic_id: number, data: AddPersonalInfoParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; lic_id?: number; message?: string }> => {
    dispatch(setLoader(true));
    try {
      const response = await updatePersonalInfoAdmin(lic_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Personal detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setLicId(response.data));
        }
        return { success: true, lic_id: response.data ?? lic_id };
      }
      const msg = response.message ?? "Failed to update personal detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

/**
 * Full KYC document flow: save personal info → get lic_id → upload each file to S3 (kyc-documents)
 * → call addlicdocument_admin with doc_typeid 1, 2, 3... in order.
 * Use when you have personal payload + list of files to process sequentially.
 */
export const runKycDocumentFlowThunk =
  (personalPayload: AddPersonalInfoParams, files: File[]) =>
  async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; lic_id?: number; message?: string }> => {
    dispatch(setLoader(true));
    try {
      const result = await runKycDocumentFlow(personalPayload, files);
      if (result.success && result.lic_id != null) {
        dispatch(setLicId(result.lic_id));
        dispatch(
          successMsg({
            message: "Personal info and documents saved successfully.",
            id: Date.now(),
          })
        );
        return { success: true, lic_id: result.lic_id };
      }
      dispatch(
        errorMsg({
          message: result.message ?? "KYC document flow failed.",
          id: Date.now(),
        })
      );
      return { success: false, message: result.message };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "KYC document flow failed.";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const getPersonalInfoAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await getPersonalInfoAdmin(lic_id);
      if (response.success) {
        dispatch(setPersonalInfoData(response.data));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch personal info.",
            id: Date.now(),
          })
        );
        dispatch(setPersonalInfoData(null));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setPersonalInfoData(null));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const getIdentificationAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await getIdentificationAdmin(lic_id);
      if (response.success) {
        const record = response.data;
        dispatch(setIdentificationData(record));
        const id = record?.id != null ? Number(record.id) : NaN;
        dispatch(setIdentificationId(Number.isFinite(id) ? id : null));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch identification.",
            id: Date.now(),
          })
        );
        dispatch(setIdentificationData(null));
        dispatch(setIdentificationId(null));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setIdentificationData(null));
      dispatch(setIdentificationId(null));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicIdentificationAdminThunk =
  (data: AddLicIdentificationParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    identification_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicIdentificationAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Identification detail saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setIdentificationId(response.data));
        return { success: true, identification_id: response.data };
      }
      const msg = response.message ?? "Failed to save identification detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicIdentificationAdminThunk =
  (identification_id: number, data: UpdateLicIdentificationParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    identification_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicIdentificationAdmin(identification_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Identification detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setIdentificationId(response.data));
        }
        return { success: true, identification_id: response.data ?? identification_id };
      }
      const msg = response.message ?? "Failed to update identification detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicEmploymentAdminThunk =
  (data: AddLicEmploymentParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    employment_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicEmploymentAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Employment detail saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setEmploymentId(response.data));
        return { success: true, employment_id: response.data };
      }
      const msg = response.message ?? "Failed to save employment detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicEmploymentAdminThunk =
  (employment_id: number, data: UpdateLicEmploymentParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    employment_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicEmploymentAdmin(employment_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Employment detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setEmploymentId(response.data));
        }
        return { success: true, employment_id: response.data ?? employment_id };
      }
      const msg = response.message ?? "Failed to update employment detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicEmploymentAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicEmploymentAdmin(lic_id);
      if (response.success) {
        const list = response.data ?? [];
        dispatch(setEmploymentData(list));
        const firstId = Array.isArray(list) && list[0]?.id != null ? Number(list[0].id) : NaN;
        dispatch(setEmploymentId(Number.isFinite(firstId) ? firstId : null));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch employment list.",
            id: Date.now(),
          })
        );
        dispatch(setEmploymentData([]));
        dispatch(setEmploymentId(null));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setEmploymentData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicOtherPolicyAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicOtherPolicyAdmin(lic_id);
      if (response.success) {
        const list = response.data ?? [];
        dispatch(setOtherPolicyData(list));
        const firstId = Array.isArray(list) && list[0]?.id != null ? Number(list[0].id) : NaN;
        dispatch(setOtherPolicyId(Number.isFinite(firstId) ? firstId : null));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch other policy list.",
            id: Date.now(),
          })
        );
        dispatch(setOtherPolicyData([]));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setOtherPolicyData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicInsuranceHistoryAdminThunk =
  (data: LicInsuranceHistory) =>
  async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; message?: string }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicInsuranceHistoryAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "Insurance history saved successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setOtherPolicyId(response.data));
        }
        return { success: true };
      }
      const msg = response.message ?? "Failed to save insurance history.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicOtherPolicyAdminThunk =
  (data: AddLicOtherPolicyParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    policy_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicOtherPolicyAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Policy detail saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setOtherPolicyId(response.data));
        return { success: true, policy_id: response.data };
      }
      const msg = response.message ?? "Failed to save policy detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicOtherPolicyAdminThunk =
  (policy_id: number, data: UpdateLicOtherPolicyParams | (LicInsuranceHistory & { status?: string })) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    policy_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicOtherPolicyAdmin(policy_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Policy detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setOtherPolicyId(response.data));
        }
        return { success: true, policy_id: response.data ?? policy_id };
      }
      const msg = response.message ?? "Failed to update policy detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicNomineeAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicNomineeAdmin(lic_id);
      if (response.success) {
        const list = response.data ?? [];
        dispatch(setNomineeData(list));
        const firstId = Array.isArray(list) && list[0]?.id != null ? Number(list[0].id) : NaN;
        dispatch(setNomineeId(Number.isFinite(firstId) ? firstId : null));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch nominee list.",
            id: Date.now(),
          })
        );
        dispatch(setNomineeData([]));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setNomineeData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicNomineeAdminThunk =
  (data: AddLicNomineeParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    nominee_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicNomineeAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "Nominee saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setNomineeId(response.data));
        return { success: true, nominee_id: response.data };
      }
      const msg = response.message ?? "Failed to save nominee.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicNomineeAdminThunk =
  (nominee_id: number, data: UpdateLicNomineeParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    nominee_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicNomineeAdmin(nominee_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Nominee detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setNomineeId(response.data));
        }
        return { success: true, nominee_id: response.data ?? nominee_id };
      }
      const msg = response.message ?? "Failed to update nominee detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicHealthAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicHealthAdmin(lic_id);
      if (response.success) {
        const list = response.data ?? [];
        dispatch(setHealthData(list));
        const firstId = Array.isArray(list) && list[0]?.id != null ? Number(list[0].id) : NaN;
        dispatch(setHealthId(Number.isFinite(firstId) ? firstId : null));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch health list.",
            id: Date.now(),
          })
        );
        dispatch(setHealthData([]));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setHealthData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicHealthAdminThunk =
  (data: AddLicHealthParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    health_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicHealthAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Health details saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setHealthId(response.data));
        return { success: true, health_id: response.data };
      }
      const msg = response.message ?? "Failed to save health details.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicHealthAdminThunk =
  (health_id: number, data: UpdateLicHealthParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    health_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicHealthAdmin(health_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Health detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setHealthId(response.data));
        }
        return { success: true, health_id: response.data ?? health_id };
      }
      const msg = response.message ?? "Failed to update health detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicLegalAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicLegalAdmin(lic_id);
      if (response.success) {
        const list = response.data ?? [];
        dispatch(setLegalData(list));
        const firstId = Array.isArray(list) && list[0]?.id != null ? Number(list[0].id) : NaN;
        dispatch(setLegalId(Number.isFinite(firstId) ? firstId : null));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch legal list.",
            id: Date.now(),
          })
        );
        dispatch(setLegalData([]));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setLegalData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicLegalAdminThunk =
  (data: AddLicLegalParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    legal_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicLegalAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Legal details saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setLegalId(response.data));
        return { success: true, legal_id: response.data };
      }
      const msg = response.message ?? "Failed to save legal details.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicLegalAdminThunk =
  (legal_id: number, data: UpdateLicLegalParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    legal_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicLegalAdmin(legal_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message:
              response.message ?? "Legal detail updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setLegalId(response.data));
        }
        return { success: true, legal_id: response.data ?? legal_id };
      }
      const msg = response.message ?? "Failed to update legal detail.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const getLicDetailByIdThunk =
  (proposal_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await getLicDetailById(proposal_id);
      if (response.success) {
        dispatch(setLicDetailData(response.data));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch LIC detail.",
            id: Date.now(),
          })
        );
        dispatch(setLicDetailData(null));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setLicDetailData(null));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateNewLicProposalAdminThunk =
  (proposal_id: number, data: UpdateNewLicProposalParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; message?: string }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateNewLicProposalAdmin(proposal_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "Proposal details updated.",
            id: Date.now(),
          })
        );
        return { success: true };
      }
      const msg = response.message ?? "Failed to update proposal details.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const addLicDocumentAdminThunk =
  (data: AddLicDocumentParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    document_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await addLicDocumentAdmin(data);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "LIC Document saved successfully.",
            id: Date.now(),
          })
        );
        dispatch(setDocumentId(response.data));
        return { success: true, document_id: response.data };
      }
      const msg = response.message ?? "Failed to save LIC document.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const updateLicDocumentAdminThunk =
  (document_id: number, data: UpdateLicDocumentParams) =>
  async (
    dispatch: AppDispatch
  ): Promise<{
    success: boolean;
    document_id?: number;
    message?: string;
  }> => {
    dispatch(setLoader(true));
    try {
      const response = await updateLicDocumentAdmin(document_id, data);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "LIC Document updated successfully.",
            id: Date.now(),
          })
        );
        if (response.data != null) {
          dispatch(setDocumentId(response.data));
        }
        return { success: true, document_id: response.data ?? document_id };
      }
      const msg = response.message ?? "Failed to update LIC document.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const deleteLicDocumentAdminThunk =
  (document_id: number) =>
  async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; message?: string }> => {
    dispatch(setLoader(true));
    try {
      const response = await deleteLicDocumentAdmin(document_id);
      if (response.success) {
        dispatch(
          successMsg({
            message: response.message ?? "LIC Document deleted successfully.",
            id: Date.now(),
          })
        );
        return { success: true };
      }
      const msg = response.message ?? "Failed to delete LIC document.";
      dispatch(errorMsg({ message: msg, id: Date.now() }));
      return { success: false, message: msg };
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { success: false, message };
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicDocumentsAdminThunk =
  (data: ListLicDocumentsParams) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicDocumentsAdmin(data);
      if (response.success) {
        dispatch(setDocumentData(response.data ?? []));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch document list.",
            id: Date.now(),
          })
        );
        dispatch(setDocumentData([]));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setDocumentData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicDocumentTypeAdminThunk =
  (lic_id: number) => async (dispatch: AppDispatch) => {
    dispatch(setLoader(true));
    try {
      const response = await listLicDocumentTypeAdmin(lic_id);
      if (response.success) {
        dispatch(setDocumentTypeData(response.data ?? []));
      } else {
        dispatch(
          errorMsg({
            message: response.message ?? "Failed to fetch document types.",
            id: Date.now(),
          })
        );
        dispatch(setDocumentTypeData([]));
      }
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Something went wrong";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setDocumentTypeData([]));
    } finally {
      dispatch(setLoader(false));
    }
  };

export const fetchLicProposalListThunk =
  (params?: {
    page?: number;
    per_page?: number;
    from_date?: string;
    to_date?: string;
    status?: string;
    proposal_number?: string;
    chief_agent_name?: string;
    chief_agent_number?: string;
    proposal_id?: number;
    user_id?: number;
    customer_mobile?: string;
  }) =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoadingProposals(true));
    try {
      const result = await listLicProposalAdmin(params ?? {});
      dispatch(
        setProposalList({
          proposals: result.data ?? [],
          pagination: {
            page: params?.page ?? 1,
            per_page: params?.per_page ?? 10,
            total: result.total,
            total_pages: result.total_pages,
            total_active: result.total_active,
            total_inactive: result.total_inactive,
          },
        })
      );
      return result;
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Failed to load proposals";
      dispatch(errorMsg({ message, id: Date.now() }));
      dispatch(setProposalList({ proposals: [], pagination: {} }));
    } finally {
      dispatch(setLoadingProposals(false));
    }
  };

export const deleteLicProposalAdminThunk =
  (proposal_id: number) => async (dispatch: AppDispatch) => {
    try {
      const result = await deleteLicProposalAdmin(proposal_id);
      if (result?.status && String(result.status).toLowerCase() === "failed") {
        dispatch(
          errorMsg({
            message: result.message ?? "Unable to delete lic proposal.",
            id: Date.now(),
          })
        );
        return result;
      }
      dispatch(successMsg({ message: result?.message ?? "Lic proposal has been deleted successfully", id: Date.now() }));
      return result;
    } catch (err: any) {
      const message =
        err?.message ?? err?.response?.data?.message ?? "Unable to delete lic proposal.";
      dispatch(errorMsg({ message, id: Date.now() }));
      return { status: "Failed", message };
    }
  };
